<template>
  <div id="page-plants-list">
    <div class="vx-card p-6">
      <div class="vx-row mb-5 mt-5">
        <div class="vx-col w-full text-center">
          <div class="inline-flex w-full md:w-2/3">
            <h5 class="w-3/12 pt-8">Move plants to batch: &nbsp;</h5>
            <div class="w-6/12 text-left">
              <label>Batch Number</label>
              <select-2
                autocomplete
                class="w-full"
                :value="form.batchId"
                @input="(item) => (form.batchId = item && item.value)"
                label-placeholder="Batch"
                name="Batch"
                placeholder="Select Batch"
                action="batch/getBatchOptions"
                v-validate="'required'"
              />
              <span class="text-danger text-sm" v-show="errors.has('Batch')">{{ errors.first('Batch') }}</span>
            </div>
            <div class="flex pt-5 ml-2 items-center">
              <vs-button class="mt-1" id="save-plant-button" type="filled" @click="confirmSaveChanges" :disabled="!validateForm">Save</vs-button>
              <div class="ml-3 text-base font-bold">
                <p>{{ form.selected.length }} Plants Selected</p>
              </div>
            </div>
          </div>
        </div>
      </div>
      <hr />
      <div class="vx-row mt-2">
        <div class="vx-col w-full items-end">
          <vs-table
            id="plant__list"
            class="vs-con-loading__container"
            stripe
            :sst="true"
            maxHeight="500px"
            @search="updateSearchQuery"
            @change-page="handleChangePage"
            @sort="handleSort"
            :total="FilteredCount"
            pagination
            :max-items="length"
            search
            :data="plants"
          >
            <template slot="header">
              <!-- TABLE ACTION ROW -->
              <div class="flex flex-wrap justify-between items-center">
                <!-- ITEMS PER PAGE -->
                <div class="mb-4 md:mb-0 mr-4 ag-grid-table-actions-left">
                  <vs-dropdown vs-trigger-click class="cursor-pointer">
                    <div class="p-4 border border-solid d-theme-border-grey-light rounded-full d-theme-dark-bg cursor-pointer flex items-center justify-between font-medium">
                      <span class="mr-2">
                        {{ page * length - (length - (FilteredCount && 1)) }}
                        -
                        {{ FilteredCount - page * length > 0 ? page * length : FilteredCount }}
                        of {{ FilteredCount }} (filtered from {{ total }} total entries)
                      </span>
                      <feather-icon icon="ChevronDownIcon" svgClasses="h-4 w-4" />
                    </div>
                    <vs-dropdown-menu>
                      <vs-dropdown-item @click="handleChangeLength(10)">
                        <span>10</span>
                      </vs-dropdown-item>
                      <vs-dropdown-item @click="handleChangeLength(20)">
                        <span>20</span>
                      </vs-dropdown-item>
                      <vs-dropdown-item @click="handleChangeLength(50)">
                        <span>50</span>
                      </vs-dropdown-item>
                      <vs-dropdown-item @click="handleChangeLength(100)">
                        <span>100</span>
                      </vs-dropdown-item>
                      <vs-dropdown-item @click="handleChangeLength(1000)">
                        <span>1000</span>
                      </vs-dropdown-item>
                    </vs-dropdown-menu>
                  </vs-dropdown>
                </div>

                <div class="mb-4 md:mb-0 mr-4 ag-grid-table-actions-left">
                  <vs-row vs-justify="flex-end">
                    <vx-tooltip text="Export Excel">
                      <vs-button class="mr-1" type="filled" color="primary" icon-pack="feather" icon="icon-download" @click="exportExcel" />
                    </vx-tooltip>
                  </vs-row>
                </div>
              </div>
            </template>

            <template slot="thead">
              <vs-th><vs-checkbox :checked="selectedAll" @input="(e) => changeInput()"></vs-checkbox></vs-th>
              <vs-th sort-key="batch.batch_name">Batch Name</vs-th>
              <vs-th sort-key="batch.batch_number">Batch Number</vs-th>
              <vs-th sort-key="batch_plant.plant_name">Strain Name</vs-th>
              <vs-th sort-key="batch_plant.serialNumber">Serial Number</vs-th>
              <vs-th sort-key="batch_plant.plant_stage">Stage</vs-th>
              <vs-th sort-key="batch_plant.room_type">Room Type</vs-th>
              <vs-th sort-key="batch_plant.grow_method">Grow Method</vs-th>
              <vs-th>Action</vs-th>
            </template>

            <template slot-scope="{ data }" ref="tableBody">
              <vs-tr :data="tr" :key="i" v-for="(tr, i) in data">
                <vs-td>
                  <vs-checkbox v-model="form.selected" v-if="!tr.product_plants.length" :vs-value="tr.id"></vs-checkbox>
                  <vx-tooltip
                    text="A checkbox is not available for this plant because it's currently listed for sale in the Product List. To move this plant to another batch, remove it from the Product listing first."
                    v-else
                  >
                    <feather-icon class="cursor-pointer" style="margin-left: 30px" icon="AlertCircleIcon" />
                  </vx-tooltip>
                </vs-td>
                <vs-td>
                  {{ tr.batch && tr.batch.batch_name }}
                </vs-td>
                <vs-td>
                  {{ tr.batch && tr.batch.batch_number }}
                </vs-td>
                <vs-td>
                  {{ tr.plant_name }}
                </vs-td>
                <vs-td>
                  {{ tr.serialNumber }}
                </vs-td>
                <vs-td>
                  {{ getPlantStage(tr.plant_stage) }}
                </vs-td>
                <vs-td>
                  {{ tr.room_type }}
                </vs-td>
                <vs-td>
                  {{ tr.grow_method }}
                </vs-td>
                <vs-td :data="tr.id">
                  <div
                    class="inline-flex"
                    v-if="checkPermissionByIds([permissions.BATCH_PLANT_EDIT]) || checkPermissionByIds([permissions.BATCH_PLANT_DELETE])"
                    :style="{ direction: $vs.rtl ? 'rtl' : 'ltr' }"
                  >
                    <vx-tooltip v-if="checkPermissionByIds([permissions.BATCH_PLANT_EDIT])" text="Edit Plant">
                      <feather-icon icon="Edit3Icon" svgClasses="h-5 w-5 mr-4 hover:text-primary cursor-pointer" @click="sendToEditPage(tr.id)" />
                    </vx-tooltip>
                    <vx-tooltip v-if="checkPermissionByIds([permissions.BATCH_PLANT_DELETE])" text="Delete Plant">
                      <feather-icon icon="Trash2Icon" svgClasses="h-5 w-5  hover:text-danger cursor-pointer" @click="deleteRecord(tr.id)" />
                    </vx-tooltip>
                  </div>
                  <div v-else>-</div>
                </vs-td>
              </vs-tr>
            </template>
          </vs-table>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex'
import XLSX from 'xlsx'
import Select2 from '@/components/custom/form-elements/Select2.vue'
import { plantStages } from '@/utils/batch-options'

export default {
  name: 'PlantList',
  data: () => ({
    order: [],
    length: 10,
    page: 1,
    search: '',
    form: {
      batchId: null,
      selected: []
    },
    loading: false
  }),

  computed: {
    ...mapState('batch', {
      batch: 'batch',
      plants: 'plant-plants',
      total: 'plant-total',
      FilteredCount: 'plant-FilteredCount'
    }),
    ...mapState('permission', ['permissions']),
    ...mapGetters('auth', ['checkPermissionByIds']),
    validateForm() {
      return !this.errors.any()
    },
    selectedAll() {
      const filteredPlant = this.plants.filter((item) => !item.product_plants.length)
      return filteredPlant.length === this.form.selected.length
    },
    getPlantStage() {
      // eslint-disable-next-line init-declarations
      let s
      return (stage) => stage && (s = plantStages.find((s) => stage === s.value)) && s.label
    }
  },

  methods: {
    ...mapActions('batch', { getBatchPlants: 'getBatchPlants', deleteBatchPlantAsync: 'deleteBatchPlant', updateBatchOfPlants: 'updateBatchOfPlants' }),
    changeInput() {
      // remove the plant which is used into product.
      const filteredPlant = this.plants.filter((item) => !item.product_plants.length)
      if (filteredPlant.length !== this.form.selected.length) {
        this.form.selected = []

        filteredPlant.map((plant) => {
          this.form.selected.push(plant.id)
        })
      } else {
        this.form.selected = []
      }
    },
    deleteRecord(id) {
      this.$vs.dialog({
        type: 'confirm',
        color: 'danger',
        title: 'Confirm Delete',
        text: 'Are you sure, you want to delete this record?',
        accept: () => this.deleteBatchPlant(id),
        acceptText: 'Delete'
      })
    },
    deleteBatchPlant(id) {
      this.loading = true
      this.deleteBatchPlantAsync(id)
        .then(() => {
          if (this.page !== 1) {
            this.page -= 1
          }
          this.loading = false
          this.$vs.notify({
            title: 'Success',
            text: 'Batch deleted successfully!',
            iconPack: 'feather',
            icon: 'icon-alert-circle',
            color: 'success'
          })
          this.getData()
        })
        .catch((err) => {
          const { message } = err
          this.loading = false
          this.$vs.notify({
            title: 'Error',
            text: message,
            iconPack: 'feather',
            icon: 'icon-alert-circle',
            color: 'danger'
          })
        })
    },
    handleChangeLength(length) {
      this.page = 1
      this.length = length
      this.getData()
    },
    handleChangePage(page) {
      this.page = page
      this.getData()
    },
    handleSort(key, active) {
      if (!key) return
      this.page = 1
      this.order = [key, active === 'desc' ? 'DESC' : 'ASC']
      this.getData()
    },
    updateSearchQuery(val) {
      this.page = 1
      this.search = val
      this.getData()
    },
    getData() {
      this.form.selected = []
      const { page, length, order, search } = this
      this.loading = true
      this.getBatchPlants({
        list: 'plant',
        data: {
          page,
          limit: length,
          order,
          search,
          plantStages: ['seedling', 'pre_veg', 'veg', 'flower', 'drying', 'dried', 'mother', 'male']
        }
      }).finally(() => {
        this.loading = false
      })
    },
    exportExcel() {
      const wb = XLSX.utils.book_new()
      const ws = XLSX.utils.json_to_sheet(
        this.plants.map((plant) => ({
          'Batch Name': plant.batch ? plant.batch.batch_name : '',
          'Batch Number': plant.batch ? plant.batch.batch_number : '',
          'Strain Name': plant.plant_name,
          'Serial Number': plant.serialNumber,
          Stage: plant.plant_stage,
          'Room Type': plant.room_type,
          'Grow Method': plant.grow_method
        }))
      )
      XLSX.utils.book_append_sheet(wb, ws, 'Sheet 1')
      XLSX.writeFile(wb, 'Plant List.xlsx')
    },
    async confirmSaveChanges() {
      this.$vs.dialog({
        type: 'confirm',
        color: 'warning',
        title: 'Are you sure?',
        text: 'Are you sure, you want to move plants into this batch?',
        accept: () => this.save_changes(),
        acceptText: 'Confirm'
      })
    },
    async save_changes() {
      if (!(await this.$validator.validate()) || !this.form.selected.length) {
        const msg = this.errors.any() ? 'Please select batch' : 'Please select at-least One Plant'
        this.$vs.notify({
          title: 'Error',
          text: msg,
          iconPack: 'feather',
          icon: 'icon-alert-circle',
          color: 'danger'
        })
        return
      }
      this.loading = true
      try {
        const { message } = await this.updateBatchOfPlants({
          id: this.form.batchId,
          data: {
            batchPlantIds: this.form.selected
          }
        })
        this.form.batchId = null
        this.loading = false
        this.getData()
        this.$vs.notify({
          title: 'Success',
          text: message,
          iconPack: 'feather',
          icon: 'icon-alert-circle',
          color: 'success'
        })
      } catch ({ message }) {
        this.loading = false
        this.$vs.notify({
          title: 'Error',
          text: message,
          iconPack: 'feather',
          icon: 'icon-alert-circle',
          color: 'danger'
        })
      }
    },
    sendToEditPage(id) {
      this.$router.push(`/plants/${id}/edit`)
    }
  },
  mounted() {
    this.getData()
  },
  watch: {
    loading() {
      if (this.loading) {
        this.$vs.loading({
          container: '#plant__list',
          scale: 0.45
        })
      } else {
        this.$vs.loading.close('#plant__list > .con-vs-loading')
      }
    }
  },
  components: {
    Select2
  }
}
</script>
